import { useState } from "react";
import { Button, Input, Label, Tabs, TabsContent, TabsList, TabsTrigger } from "@wa/ui";
import { Check, Copy, ExternalLink } from "lucide-react";
import { useT, useI18n } from "../../../i18n/I18nProvider";
import { SITE_URL } from "../../../lib/auth";
import { AI_APP_NAMES, AI_APPS, codexCommand, mcpAddress, mcpGuidePath, type AiApp } from "./lib";

/** A read-only value with a Copy button: the address and the Codex command. */
function CopyRow({ id, label, value }: { id: string; label: string; value: string }) {
  const t = useT();
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // No clipboard (an old browser, a blocked permission): the field can still be selected.
    }
  }

  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-xs text-muted-foreground">
        {label}
      </Label>
      <div className="flex flex-col gap-2 sm:flex-row">
        <Input id={id} readOnly value={value} className="font-mono text-sm" onFocus={(e) => e.currentTarget.select()} />
        <Button variant="outline" className="shrink-0" onClick={() => void copy()}>
          {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
          {copied ? t("security.aiApps.copied") : t("security.aiApps.copy")}
        </Button>
      </div>
    </div>
  );
}

/**
 * Where a person finds how to connect an AI app (the admin's Integrations card shows the
 * same address and steps): the one address Claude, ChatGPT and Codex all use, and a tab of
 * short steps per app. The list of connected apps above only fills after they have.
 */
export function ConnectAiApp() {
  const t = useT();
  const { locale } = useI18n();
  const [app, setApp] = useState<AiApp>("claude");
  const address = mcpAddress(window.location);

  return (
    <div className="space-y-3 border-t pt-4">
      <p className="text-sm font-medium">{t("security.aiApps.connectTitle")}</p>
      <p className="text-sm text-muted-foreground">{t("security.aiApps.connectHint")}</p>
      <CopyRow id="mcp-address" label={t("security.aiApps.address")} value={address} />
      <Tabs value={app} onValueChange={(v) => setApp(v as AiApp)}>
        <TabsList className="w-full sm:w-auto">
          {AI_APPS.map((a) => (
            <TabsTrigger key={a} value={a} className="flex-1 sm:flex-none">
              {AI_APP_NAMES[a]}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="claude" className="pt-1">
          <p className="text-sm text-muted-foreground">{t("security.aiApps.claudeSteps")}</p>
        </TabsContent>
        <TabsContent value="chatgpt" className="space-y-1 pt-1">
          <p className="text-sm text-muted-foreground">{t("security.aiApps.chatgptSteps")}</p>
          <p className="text-xs text-muted-foreground">{t("security.aiApps.chatgptPlan")}</p>
        </TabsContent>
        <TabsContent value="codex" className="space-y-2 pt-1">
          <p className="text-sm text-muted-foreground">{t("security.aiApps.codexSteps")}</p>
          <CopyRow id="codex-command" label={t("security.aiApps.command")} value={codexCommand(address)} />
        </TabsContent>
      </Tabs>
      <a
        href={`${SITE_URL}${mcpGuidePath(locale, app)}`}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
      >
        <ExternalLink className="size-3.5" />
        {t("security.aiApps.guide")}
      </a>
    </div>
  );
}
